import React, { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Lottie from 'lottie-react';
import { isNil } from 'lodash-es';
import { toast } from 'react-toastify';
import loader from '../../../assets/lotties/loader.json';
import { useCreateCategoryMutation, useGetCategoryByIdQuery, useUpdateCategoryMutation } from '../../../store/menu/menuApi';
import { IGetCategoryResponse } from '../../../proto/category/category';
import './UpsertCategory.scss';
import { ensureDataUrl, fileToDataUrl, stripDataUrl } from '../../../utils/imageHandlers';

type Inputs = {
	title: string;
	description: string;
	image: string;
};

const schema = yup.object({
	title: yup.string().required('Category title is required'),
	description: yup.string().required('Category description is required'),
	image: yup.string().nullable(), // add extra tests if you want
});

interface IUpsertCategoryProps {
	menuId: string;
	categoryId?: string;
	onSubmit: (data: Inputs) => void;
	onCancel: () => void;
}

export const UpsertCategory = ({ menuId, categoryId, onSubmit, onCancel }: IUpsertCategoryProps): JSX.Element => {
	const { data: category, isLoading: isLoadingCategory } = useGetCategoryByIdQuery({ categoryId, menuId }, { skip: isNil(categoryId) });

	const [createCategory] = useCreateCategoryMutation();
	const [updateCategory] = useUpdateCategoryMutation();

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm<Inputs>({
		resolver: yupResolver(schema),
		defaultValues: {
			title: category?.title || '',
			description: category?.description || '',
			// if server returns base64, show it as image:
			image: ensureDataUrl(category?.image ?? null),
		},
	});

	// reflect fetched category into the form
	useEffect(() => {
		if (!isNil(category)) {
			reset({
				title: category?.title || '',
				description: category?.description || '',
				image: ensureDataUrl(category?.image ?? null),
			});

			// also clear the physical file input so the UI is synced
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	}, [category, reset]);

	const previewSrc = watch('image') ?? null;

	const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
		const file = e.target.files?.[0];

		if (!file) {
			return;
		}

		// Optional guards
		if (!file.type.startsWith('image/')) {
			toast.error('נא לבחור תמונת תמיכה בלבד');
			e.target.value = '';

			return;
		}

		// size guard (e.g. <= 2MB)
		if (file.size > 2 * 1024 * 1024) {
			toast.error('התמונה גדולה מדי (עד 2MB)');
			e.target.value = '';

			return;
		}

		try {
			const dataUrl = await fileToDataUrl(file);

			setValue('image', dataUrl, { shouldValidate: true, shouldDirty: true });
		} catch (err) {
			toast.error('שגיאה בקריאת קובץ התמונה');
		}
	};

	const removeImage = (): void => {
		setValue('image', null, { shouldDirty: true, shouldValidate: true });

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleUpsertCategory = async (data: Inputs): Promise<IGetCategoryResponse> => {
		// If your backend expects *plain* base64, strip the data URL prefix.
		const payloadImage = stripDataUrl(data.image ?? null);
		const body = { menuId, categoryId, ...data, image: payloadImage };

		if (!isNil(categoryId)) {
			return updateCategory(body).unwrap();
		}

		return createCategory(body).unwrap();
	};

	const onFormSubmit: SubmitHandler<Inputs> = (data) => {
		handleUpsertCategory(data)
			.then((res) => {
				const status = isNil(categoryId) ? 'נוספה' : 'עודכנה';

				toast.success(`הקטגוריה ${status} בהצלחה`);

				// reflect what server saved (use ensureDataUrl for preview)
				reset({
					title: res.title ?? data.title,
					description: res.description ?? data.description,
					image: ensureDataUrl((res as any).image ?? data.image ?? null),
				});

				onSubmit(data);
			})
			.catch((err) => {
				console.error('Failed to upsert category:', err);
				toast.error('שגיאת עדכון');
			});
	};

	return (
		<div className="upsertCategoryContainer">
			{isLoadingCategory ? (
				<Lottie animationData={loader} loop style={{ width: '10rem' }} />
			) : (
				<form onSubmit={handleSubmit(onFormSubmit)}>
					<div className="formGroup">
						<label>שם הקטגוריה</label>
						<input {...register('title')} />
						{errors.title && <p style={{ color: 'red' }}>{errors.title.message}</p>}
					</div>

					<div className="formGroup">
						<label>תיאור הקטגוריה</label>
						<input {...register('description')} />
						{errors.description && <p style={{ color: 'red' }}>{errors.description.message}</p>}
					</div>

					{/* IMAGE UPLOAD */}
					<div className="formGroup">
						<label>תמונה</label>
						<input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} />
						{previewSrc && (
							<div className="imagePreview">
								<img
									src={ensureDataUrl(previewSrc) as string}
									alt="Preview"
									style={{ maxWidth: '220px', borderRadius: 8, display: 'block', marginTop: 8 }}
								/>
								<div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
									<button type="button" onClick={(): void => fileInputRef.current?.click()}>
										החלף תמונה
									</button>
									<button type="button" onClick={removeImage}>
										הסר תמונה
									</button>
								</div>
							</div>
						)}
						{/* keep image wired into the form (hidden) */}
						<input type="hidden" {...register('image')} />
						{errors.image && <p style={{ color: 'red' }}>{errors.image.message}</p>}
					</div>

					<button type="submit" className="formHandlerButton saveForm">
						שמור
					</button>
					<button type="button" className="formHandlerButton cancelForm" onClick={onCancel}>
						בטל
					</button>
				</form>
			)}
		</div>
	);
};
